// src/components/Clientes.tsx
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Filter, ArrowUpDown, ArrowUp, ArrowDown, Phone, Calendar, Building2, User, Package } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClientDetails from "@/components/ClientDetails/ClientDetails";
import AdicionarLead from "@/components/ClientDetails/AdicionarLead";
import { ExtendedLead } from '@/components/ClientDetails/types';
import { supabase } from '@/integrations/supabase/client';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { leadProdutoBadgeMeta } from '@/constants/leadsProduto';
import { cn } from '@/lib/utils';

const formatDate = (dateStr: string | undefined) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
};

const formatPhone = (phone: string | undefined) => {
  if (!phone) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  // Formata no padrão brasileiro (00) 0000-0000
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  // Para números com 11 dígitos (celular com 9 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Retorna o número original se não conseguir formatar
  return phone;
};

const getStatusBadgeClass = (status: string | undefined) => {
  const statusLower = (status || 'Lead').toLowerCase();
  if (statusLower === 'lead') {
    // Mesma cor da etapa "QUALIFICAÇÃO DE LEAD" do pipeline
    return 'bg-blue-200 text-blue-800';
  }
  if (statusLower === 'lead-fb') {
    // Mesma cor da etapa "FOLLOW-UP" do pipeline (#78B478)
    return 'text-white';
  }
  if (statusLower === 'cliente') {
    // Cor da etapa "NEGÓCIO FECHADO" do pipeline
    return 'bg-blue-600 text-white';
  }
  if (statusLower === 'lead perdido') {
    // Cor da etapa "LEADS PERDIDOS" do pipeline
    return 'bg-red-300 text-red-800';
  }
  return '';
};

const getStatusBadgeStyle = (status: string | undefined) => {
  const statusLower = (status || 'Lead').toLowerCase();
  if (statusLower === 'lead-fb') {
    // Cor customizada do FOLLOW-UP (#78B478)
    return { backgroundColor: '#78B478' };
  }
  return {};
};

export default function Clientes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clientes, setClientes] = useState<ExtendedLead[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<ExtendedLead | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState({ status: '', origem: '', vendedor: '' });
  
  // Hook para verificar permissões e role do usuário
  const permissions = usePermissions();
  const { userProfile } = useAuthorization();
  const isVendedor = permissions.userRole === 'vendedor' || permissions.userProfile?.role === 'vendedor';
  const isParceiro = permissions.userRole === 'parceiro' || permissions.userProfile?.role === 'parceiro';
  const mostraColunaParceiro = isVendedor || isParceiro;
  /** Parceiro pai: pode ver leads da rede, sem editar */
  const isParceiroPai = isParceiro && (userProfile?.pode_criar_rede_parceiros === true);
  const isLeadFromRede = (lead: { parceiro_id?: string | null } | null) =>
    !!lead && isParceiroPai && lead.parceiro_id !== userProfile?.id;
  
  // Hook para detectar mobile
  const isMobile = useIsMobile();

  // Função para buscar leads do backend
  const fetchLeads = async () => {
    try {
      const response = await fetchWithAuth('/api/leads?crm_funil=vendas', {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar leads');
      }

      const data: ExtendedLead[] = await response.json();
      setClientes(data);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    }
  };

  // Carregar leads ao montar o componente
  useEffect(() => {
    fetchLeads();
  }, []);

  // Ler parâmetros da URL ao montar o componente
  useEffect(() => {
    const searchParam = searchParams.get('search');
    const idParam = searchParams.get('id');
    
    if (searchParam) {
      setSearchQuery(searchParam);
      setAppliedSearch(searchParam);
      // Limpar parâmetro da URL após aplicar
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Abrir detalhes do cliente quando o ID estiver na URL e os clientes forem carregados
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === idParam);
      if (cliente) {
        setSelectedCliente(cliente);
        setDetailOpen(true);
        // Limpar parâmetro da URL após aplicar
        setSearchParams({}, { replace: true });
      }
    }
  }, [clientes, searchParams, setSearchParams]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedClientes = [...clientes].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal: string = '';
    let bVal: string = '';

    switch (sortColumn) {
      case 'nome':
        aVal = a.nome || a.razaoSocial || '';
        bVal = b.nome || b.razaoSocial || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'telefone':
        aVal = a.telefone || '';
        bVal = b.telefone || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'origem':
        aVal = a.origem || '';
        bVal = b.origem || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'produto': {
        aVal = leadProdutoBadgeMeta(a.produto)?.label || a.produto || '';
        bVal = leadProdutoBadgeMeta(b.produto)?.label || b.produto || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      case 'vendedor':
        // Para vendedores e parceiros, usar parceiroNome; para outros, usar vendedorNome
        aVal = mostraColunaParceiro ? (a.parceiroNome || 'Sem Parceiro') : (a.vendedorNome || '');
        bVal = mostraColunaParceiro ? (b.parceiroNome || 'Sem Parceiro') : (b.vendedorNome || '');
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'createdAt': {
        const aDate = new Date(a.createdAt || '1970-01-01');
        const bDate = new Date(b.createdAt || '1970-01-01');
        return sortDirection === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
      }
      default:
        return 0;
    }
  });

  const filteredClientes = sortedClientes.filter(cliente => {
    const name = (cliente.nome || cliente.razaoSocial || '').toLowerCase();
    const telefone = cliente.telefone || '';
    const origem = (cliente.origem || '').toLowerCase();
    // Para vendedores e parceiros, usar parceiroNome; para outros, usar vendedorNome
    const vendedorOuParceiro = mostraColunaParceiro ? (cliente.parceiroNome || 'sem parceiro').toLowerCase() : (cliente.vendedorNome || '').toLowerCase();
    const status = (cliente.status || '').toLowerCase();
    const produto = cliente.produto || '';
    const produtoBusca =
      (leadProdutoBadgeMeta(cliente.produto)?.label || '').toLowerCase() + produto.toLowerCase();

    // Excluir leads de Carregadores Veiculares desta página
    if (produto === 'Carregadores Veiculares') return false;

    const matchesSearch = name.includes(appliedSearch.toLowerCase()) ||
                          telefone.includes(appliedSearch) ||
                          origem.includes(appliedSearch.toLowerCase()) ||
                          vendedorOuParceiro.includes(appliedSearch.toLowerCase()) ||
                          status.includes(appliedSearch.toLowerCase()) ||
                          produtoBusca.includes(appliedSearch.toLowerCase());

    const matchesFilter = (appliedFilter.status === '' || appliedFilter.status === 'todos' || status === appliedFilter.status.toLowerCase()) &&
                          (appliedFilter.origem === '' || appliedFilter.origem === 'todos' || origem === appliedFilter.origem.toLowerCase()) &&
                          (appliedFilter.vendedor === '' || appliedFilter.vendedor === 'todos' || vendedorOuParceiro === appliedFilter.vendedor.toLowerCase());

    return matchesSearch && matchesFilter;
  });

  const handleViewDetails = (cliente: ExtendedLead) => {
    setSelectedCliente(cliente);
    setDetailOpen(true);
  };

  const applyFilters = () => {
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setAppliedFilter({ status: '', origem: '', vendedor: '' });
    setFilterOpen(false);
  };

  const filtersApplied = Object.values(appliedFilter).some(val => val !== '' && val !== 'todos');

  const uniqueStatuses = [...new Set(clientes.map(c => c.status || 'Lead'))].filter(Boolean);
  const uniqueOrigens = [...new Set(clientes.map(c => c.origem || ''))].filter(Boolean);
  // Para vendedores e parceiros, usar parceiroNome; para outros, usar vendedorNome
  const uniqueVendedores = [...new Set(clientes.map(c => mostraColunaParceiro ? (c.parceiroNome || 'Sem Parceiro') : (c.vendedorNome || '')))].filter(Boolean);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Card>
        <CardHeader>
          <CardTitle>Clientes</CardTitle>
          <CardDescription>Lista de clientes cadastrados</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Barra de pesquisa e filtros - Responsiva */}
          <div className={`flex ${isMobile ? 'flex-col' : 'justify-between'} gap-3 mb-4`}>
            <form
              className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2 flex-1`}
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedSearch(searchQuery);
              }}
            >
              <Input
                placeholder="Pesquisar Nome ou Razão Social"
                className={isMobile ? "w-full" : "w-64"}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className={`flex ${isMobile ? 'flex-row' : ''} gap-2`}>
                <Button type="submit" variant="outline" className={isMobile ? "flex-1" : ""}>
                  <Search className="h-4 w-4 mr-2" /> Buscar
                </Button>
                <Button
                  type="button"
                  variant={filtersApplied ? "default" : "outline"}
                  onClick={() => setFilterOpen(true)}
                  className={isMobile ? "flex-1" : ""}
                >
                  <Filter className="h-4 w-4 mr-2" /> Filtros
                </Button>
              </div>
            </form>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button className={isMobile ? "w-full" : ""}>
                  <Plus className="h-4 w-4 mr-2" /> Adicionar Lead
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Adicionar Novo Lead</DialogTitle>
                  <DialogDescription>
                    Preencha as informações do novo lead que será adicionado ao sistema.
                  </DialogDescription>
                </DialogHeader>
                <AdicionarLead onClose={() => setIsOpen(false)} onSuccess={fetchLeads} />
              </DialogContent>
            </Dialog>
          </div>
          
          {clientes.length === 0 ? (
            <p className="text-center text-muted-foreground">Nenhum cliente cadastrado ainda.</p>
          ) : isMobile ? (
            // Vista de cards para mobile
            <div className="space-y-4">
              {filteredClientes.map((cliente, index) => {
                const produtoMeta = leadProdutoBadgeMeta(cliente.produto);
                return (
                <Card key={cliente.id || index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* Cabeçalho do card com nome e status */}
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="font-semibold text-lg flex-1">
                          {cliente.nome || cliente.razaoSocial}
                        </h3>
                        <Badge 
                          className={`${getStatusBadgeClass(cliente.status)} shrink-0`}
                          style={getStatusBadgeStyle(cliente.status)}
                        >
                          {cliente.status || 'Lead'}
                        </Badge>
                      </div>
                      
                      {/* Informações do cliente */}
                      <div className="space-y-2 text-sm text-muted-foreground">
                        {cliente.telefone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0" />
                            <span>{formatPhone(cliente.telefone)}</span>
                          </div>
                        )}
                        {cliente.createdAt && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 shrink-0" />
                            <span>{formatDate(cliente.createdAt)}</span>
                          </div>
                        )}
                        {cliente.origem && (
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 shrink-0" />
                            <span>{cliente.origem}</span>
                          </div>
                        )}
                        {produtoMeta && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Package className="h-4 w-4 shrink-0" />
                            <Badge variant="outline" className={cn('font-normal', produtoMeta.badgeClassName)}>
                              {produtoMeta.label}
                            </Badge>
                          </div>
                        )}
                        {(mostraColunaParceiro ? cliente.parceiroNome : cliente.vendedorNome) && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 shrink-0" />
                            <span>
                              {mostraColunaParceiro 
                                ? (cliente.parceiroNome || 'Sem Parceiro') 
                                : cliente.vendedorNome}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Botão de ação */}
                      <Button 
                        className="w-full mt-2" 
                        variant="outline"
                        onClick={() => handleViewDetails(cliente)}
                      >
                        Ver Detalhes
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          ) : (
            // Vista de tabela para desktop
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={`cursor-pointer ${sortColumn === 'createdAt' ? 'text-primary' : ''}`} onClick={() => handleSort('createdAt')}>
                    Data
                    {sortColumn === 'createdAt' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'nome' ? 'text-primary' : ''}`} onClick={() => handleSort('nome')}>
                    Nome/Razão Social
                    {sortColumn === 'nome' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'telefone' ? 'text-primary' : ''}`} onClick={() => handleSort('telefone')}>
                    Telefone
                    {sortColumn === 'telefone' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'origem' ? 'text-primary' : ''}`} onClick={() => handleSort('origem')}>
                    Origem
                    {sortColumn === 'origem' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'produto' ? 'text-primary' : ''}`} onClick={() => handleSort('produto')}>
                    Produto
                    {sortColumn === 'produto' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'vendedor' ? 'text-primary' : ''}`} onClick={() => handleSort('vendedor')}>
                    {mostraColunaParceiro ? 'Parceiro' : 'Vendedor'}
                    {sortColumn === 'vendedor' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'status' ? 'text-primary' : ''}`} onClick={() => handleSort('status')}>
                    Status
                    {sortColumn === 'status' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClientes.map((cliente, index) => {
                  const produtoMeta = leadProdutoBadgeMeta(cliente.produto);
                  return (
                  <TableRow key={cliente.id || index}>
                    <TableCell>{formatDate(cliente.createdAt)}</TableCell>
                    <TableCell>{cliente.nome || cliente.razaoSocial}</TableCell>
                    <TableCell>{formatPhone(cliente.telefone)}</TableCell>
                    <TableCell>{cliente.origem}</TableCell>
                    <TableCell>
                      {produtoMeta ? (
                        <Badge variant="outline" className={cn('font-normal whitespace-normal text-left', produtoMeta.badgeClassName)}>
                          {produtoMeta.label}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>{mostraColunaParceiro ? (cliente.parceiroNome || 'Sem Parceiro') : cliente.vendedorNome}</TableCell>
                    <TableCell>
                      <Badge 
                        className={getStatusBadgeClass(cliente.status)}
                        style={getStatusBadgeStyle(cliente.status)}
                      >
                        {cliente.status || 'Lead'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" onClick={() => handleViewDetails(cliente)}>Ver Detalhes</Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className={`${isMobile ? 'max-w-[95vw]' : 'max-w-4xl'} max-h-[80vh] overflow-y-auto`} aria-describedby={undefined}>
          {selectedCliente && (
            <ClientDetails
              cliente={selectedCliente} 
              onClose={() => setDetailOpen(false)} 
              onUpdate={fetchLeads}
              readOnly={isLeadFromRede(selectedCliente)}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Filtros</DialogTitle>
            <DialogDescription>
              Filtre a lista de clientes por status, origem ou vendedor/parceiro.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={appliedFilter.status} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, status: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {uniqueStatuses.map(status => (
                    <SelectItem key={status} value={status.toLowerCase()}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Origem</label>
              <Select value={appliedFilter.origem} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, origem: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione origem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {uniqueOrigens.map(origem => (
                    <SelectItem key={origem} value={origem.toLowerCase()}>{origem}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">{mostraColunaParceiro ? 'Parceiro' : 'Vendedor'}</label>
              <Select value={appliedFilter.vendedor} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, vendedor: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder={mostraColunaParceiro ? "Selecione parceiro" : "Selecione vendedor"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {uniqueVendedores.map(vendedor => (
                    <SelectItem key={vendedor} value={vendedor.toLowerCase()}>{vendedor}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={clearFilters}>Limpar Filtros</Button>
              <Button onClick={applyFilters}>Aplicar Filtros</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
